import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ── Types ──────────────────────────────────────────────────────────────────

export interface ClinicalAlert {
  id: string;
  clinic_id: string;
  patient_id: string;
  practitioner_id: string | null;
  alert_type: string;
  alert_data: {
    template_id?: string;
    template_name?: string;
    threshold?: number;
    total_score?: number;
    forms_assignment_id?: string;
    triggered_at?: string;
  };
  created_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
}

// ── Query Keys ─────────────────────────────────────────────────────────────

export const clinicalAlertsKey = (clinicId: string | null) =>
  ['clinical_alerts', clinicId] as const;

// ── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetches all unacknowledged clinical alerts for a clinic.
 * Returns alerts indexed by patient_id for fast badge lookups.
 */
export function useClinicalAlerts(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: clinicalAlertsKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ClinicalAlert[]> => {
      if (!clinicId) return [];

      const { data, error } = await supabase
        .from('clinical_alerts')
        .select('id, clinic_id, patient_id, practitioner_id, alert_type, alert_data, created_at, acknowledged_at, acknowledged_by')
        .eq('clinic_id', clinicId)
        .is('acknowledged_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as ClinicalAlert[];
    },
    staleTime: 60_000,
  });
}

/**
 * Returns a Set of patient_ids that have unacknowledged clinical alerts.
 */
export function usePatientAlertSet(clinicId: string | null | undefined): Set<string> {
  const { data } = useClinicalAlerts(clinicId);
  if (!data) return new Set();
  return new Set(data.map((a) => a.patient_id));
}

/**
 * Acknowledges a clinical alert (sets acknowledged_at and acknowledged_by via RPC).
 * The clinical_alerts table blocks direct UPDATE from clients, so we use a security-
 * definer RPC to perform the acknowledgement.
 */
export function useAcknowledgeAlert(clinicId: string | null | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (alertId: string): Promise<void> => {
      const { error } = await supabase.rpc('acknowledge_clinical_alert', {
        p_alert_id: alertId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clinicalAlertsKey(clinicId ?? null) });
    },
  });
}
